import { CanActivateFn, Router } from '@angular/router';
import { inject } from '@angular/core';
import { UserService } from '../services/user.service';
import { map, catchError, of } from 'rxjs';

/**
 * Route guard that restricts access based on permissions.
 * Accepts a single permission key or an array (OR logic — user needs at least one).
 * Redirects to the user's home route if they lack the required permission(s).
 *
 */
export const permissionGuard =
  (permissions: string | string[]): CanActivateFn =>
  () => {
    const userService: UserService = inject(UserService);
    const router: Router = inject(Router);
    const required = Array.isArray(permissions) ? permissions : [permissions];

    const checkPermissions = (user: {
      userType: string;
      hasAnyPermission: (p: string[]) => boolean;
    }) => {
      if (user.hasAnyPermission(required)) return true;
      const home = user.userType === 'faculty' ? '/counselors' : '/students';
      router.navigateByUrl(home);
      return false;
    };

    if (userService.currentUser) {
      return checkPermissions(userService.currentUser);
    }

    return userService.getCurrentUser().pipe(
      map((user) => checkPermissions(user)),
      catchError(() => {
        router.navigateByUrl('/');
        return of(false);
      }),
    );
  };
